import { findElementAll, selectedElementAll } from "../utils/element-handlers.utils";

enum AppearAnimationsTypes {
    'fade-in',
    'reveal-from-left',
    'reveal-from-right',
    'zoom-in',
    'zoom-out',
    'slide-up',
    'slide-down',
}

type AppearAnimationsType = keyof typeof AppearAnimationsTypes;

interface AppearAnimationSetup {
    animationType: AppearAnimationsType,
    reversible: boolean,
    delay: number | false,
    querySelector: string | string[]
}

const appearAttribute = 'data-appear';
const appearDelayAttribute = 'data-appear-delay';

function AppearAnimationsComponent(animationSetups: AppearAnimationSetup[]) {
    Object.keys(AppearAnimationsTypes).forEach(animationTypeString => {
        const animationType = animationTypeString as AppearAnimationsType;
        const elements = selectedElementAll(`[${appearAttribute}="${animationType}"]`);

        if (elements?.length) {
            runAnimationObserver(elements, animationType, false, 'attribute')
        }
    });

    animationSetups.forEach(animationSetup => {
        let elements: HTMLElement[] = [];

        if (typeof animationSetup.querySelector === 'string') {
            elements = elements.concat(findElementAll(animationSetup.querySelector));
        }
        else {
            animationSetup.querySelector.forEach(querySelectorItem => {
                elements = elements.concat(findElementAll(querySelectorItem));
            });
        }

        runAnimationObserver(
            elements,
            animationSetup.animationType,
            animationSetup.reversible,
            animationSetup.delay
        )
    });
}

export default function initAppearAnimationsComponent() {
    AppearAnimationsComponent([
        {
            animationType: 'reveal-from-left',
            reversible: false,
            delay: false,
            querySelector: [
                '#HomeHolder .n-card-section.n-reverse',
                '#HomeHolder .n-section-body .n-card.n-reverse'
            ]
        },
        {
            animationType: 'reveal-from-right',
            reversible: false,
            delay: false,
            querySelector: [
                '#HomeHolder .n-card-section:not(.n-reverse)',
                '#HomeHolder .n-section-body .n-card:not(.n-reverse)'
            ]
        },
        {
            animationType: 'zoom-in',
            reversible: false,
            delay: false,
            querySelector: [
                '#HomeHolder .n-section[data-n-collapsible]'
            ]
        }
    ]);
}

function runAnimationObserver(
    elements: HTMLElement[],
    animationType: AppearAnimationsType,
    isReversible: boolean,
    delay: number | false | 'attribute'
) {
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                if (delay === false) {
                    doAppearOn();
                }
                else if (typeof delay === 'number') {
                    setTimeout(doAppearOn, delay);
                }
                else {
                    const delayFromAttribute = Number(entry.target.getAttribute(appearDelayAttribute));
                
                    if (!isNaN(delayFromAttribute)) {
                        setTimeout(doAppearOn, delayFromAttribute);
                    }
                    else {
                        doAppearOn();
                    }
                }

                function doAppearOn() {
                    entry.target.classList.add('n-appear-on');
                }
            }
            else if (isReversible) {
                entry.target.classList.remove('n-appear-on');
            }
        });

        !isReversible && checkDisconnect();
    });

    elements.forEach(element => {
        element.classList.add('n-appear-off');
        element.classList.add('n-appear-' + animationType)
        observer.observe(element);
    });

    function checkDisconnect() {
        for (let i = 0; i < elements.length; i++) {
            if (!elements[i].classList.contains('n-appear-on')) {
                return;
            }
        }

        observer.disconnect();
    }
}